import { AfterViewInit, Component, ElementRef, ViewChild, inject } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { ConfirmService } from '../../services/confirm/confirm.service';
import { ConfirmDialogComponent } from '../core/dialogs/confirm-dialog.component';
import { LinkLabelComponent } from '../core/labels/link-label.component';
import { SnackbarComponent } from '../core/labels/snackbar.component';
import { BreadcrumbComponent } from './breadcrumb.component';
import { FooterComponent } from './footer.component';
import { HeaderComponent } from './header.component';

@Component({
  selector: 'app-layout',
  imports: [
    RouterOutlet,
    FooterComponent,
    HeaderComponent,
    SnackbarComponent,
    BreadcrumbComponent,
    ConfirmDialogComponent,
    LinkLabelComponent,
  ],
  template: `
    <app-link-label
      classes="skip-to-main"
      (click)="skipToMain($event)"
      ariaLabel="Skip to main content"
    >
      Skip to main content
    </app-link-label>
    <app-header />
    <div
      class="tw-w-full tw-flex tw-flex-col tw-items-center tw-min-h-[80%] tw-bg-[var(--sed-grey-20-color)]"
      style="padding-top: var(--header-height, 0px);"
    >
      <div
        class="tw-w-full tw-h-full tw-grow tw-flex tw-justify-center tw-items-stretch tw-max-w-[1300px]"
      >
        <div
          class="tw-w-full tw-max-w-[1300px] tw-h-full tw-min-h-[600px] tw-pt-2 tw-items-stretch"
        >
          <div
            id="top-of-page"
            tabindex="-1"
            class="screen-reader-text"
            aria-label="Top of page"
          ></div>
          <app-breadcrumb />
          <main
            id="main-content"
            role="main"
            #mainContent
            tabindex="-1"
            class="main-content-focus"
          >
            <router-outlet></router-outlet>
          </main>
        </div>
      </div>
    </div>

    <app-footer [techSupportLiveChat]="true" />
    <app-snackbar />
    <app-confirm-dialog
      #confirmDialog
      (confirm)="confirmService.confirm()"
      (cancel)="confirmService.cancel()"
    >
    </app-confirm-dialog>
  `,
  styles: `
    :host {
      display: block;
    }

    .main-content-focus:focus {
      outline: none;
    }
  `,
})
export class LayoutComponent implements AfterViewInit {
  protected confirmService = inject(ConfirmService);

  @ViewChild('confirmDialog')
  confirmDialog: ConfirmDialogComponent;

  @ViewChild('mainContent', { static: true })
  mainContent: ElementRef<HTMLElement>;

  ngAfterViewInit(): void {
    this.confirmService.registerDialog(this.confirmDialog);
  }

  protected skipToMain(event: Event) {
    event.preventDefault();
    const target = this.mainContent?.nativeElement;
    if (target) {
      // Focus main content without scrolling - keeps page at current position
      // This allows keyboard users to skip navigation while still seeing the tabs/content
      target.focus({ preventScroll: true });
    }
  }
}
